import React, { createContext, useEffect, useState } from "react";
import { io, Socket } from "socket.io-client";
import { Emitters } from "../service/websocket/types";
import { useEmitters } from "../service/websocket/useEmitters";
import { idFlavor } from "@specsheet-common/shared-types";
import { WebsocketMessageIdTemplate } from "@specsheet-common/shared-tools";
import { socketIoURL } from "../constants/url";
import { useAuth0 } from "@auth0/auth0-react";
import { useAccessToken } from "src/hooks/utils/useAccessToken";
import { setCurrentSocketId } from "src/service/websocket/currentSocketId";

export interface WebsocketContextProps {
  socketId: idFlavor<"WebsocketId"> | undefined;
  wsEmit: Emitters;
  socket?: Socket;
  awaitedMessageIds: Set<WebsocketMessageIdTemplate>;
  startWaitingForMessage(messageId: WebsocketMessageIdTemplate): void;
  stopWaitingForMessage(messageId: WebsocketMessageIdTemplate): void;
  isWaitingForMessage: boolean;
  setIsWaitingForMessage(data: boolean): void;
  isWaitingForDocumentDownloadMessage: {
    campaign: boolean;
    placement: boolean;
    placementOne: boolean;
  };
  setIsWaitingForDocumentDownloadMessage(
    setState: (data: {
      campaign: boolean;
      placement: boolean;
      placementOne: boolean;
    }) => {
      campaign: boolean;
      placement: boolean;
      placementOne: boolean;
    }
  ): void;
}

export const WebsocketContext = createContext<WebsocketContextProps | null>(
  null
);

export const WebsocketProvider: React.FC = ({ children }) => {
  const { getAccessToken } = useAccessToken();
  const { isAuthenticated } = useAuth0();

  const [socket, setSocket] = useState<Socket>();

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let active = true;
    let nextSocket: Socket | undefined;

    void (async () => {
      const token = await getAccessToken();
      if (!active) {
        return;
      }
      nextSocket = io(socketIoURL, {
        withCredentials: true,
        extraHeaders: {
          Authorization: token,
        },
      });
      setSocket(nextSocket);
    })();

    return () => {
      active = false;
      nextSocket?.disconnect();
      setCurrentSocketId(undefined);
    };
  }, [getAccessToken, isAuthenticated]);

  const [awaitedMessageIds, setAwaitedMessageIds] = useState<
    Set<WebsocketMessageIdTemplate>
  >(new Set());
  const [isWaitingForMessage, setIsWaitingForMessage] = useState(false);
  const [
    isWaitingForDocumentDownloadMessage,
    setIsWaitingForDocumentDownloadMessage,
  ] = useState({
    campaign: false,
    placement: false,
    placementOne: false,
  });
  const [socketId, setSocketId] = useState<string>();

  useEffect(() => {
    if (!socket) {
      setCurrentSocketId(undefined);
      return;
    }

    const onConnect = () => {
      setSocketId(socket.id);
      setCurrentSocketId(socket.id);
    };
    const onDisconnect = () => {
      setSocketId(undefined);
      setCurrentSocketId(undefined);
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    if (socket.connected && socket.id) {
      onConnect();
    }

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
    };
  }, [socket]);

  const startWaitingForMessage = (messageId: WebsocketMessageIdTemplate) => {
    setAwaitedMessageIds(new Set([...awaitedMessageIds, messageId]));
  };

  const stopWaitingForMessage = (messageId: WebsocketMessageIdTemplate) => {
    const newSet = new Set(awaitedMessageIds);
    newSet.delete(messageId);
    setAwaitedMessageIds(newSet);
  };

  const emitters = useEmitters({ socket });

  return (
    <WebsocketContext.Provider
      value={{
        socketId,
        socket,
        wsEmit: emitters,
        awaitedMessageIds,
        startWaitingForMessage,
        stopWaitingForMessage,
        isWaitingForMessage,
        setIsWaitingForMessage,
        isWaitingForDocumentDownloadMessage,
        setIsWaitingForDocumentDownloadMessage,
      }}
    >
      {children}
    </WebsocketContext.Provider>
  );
};
